<template>
  <div class="calendar-view">
    <CalendarMonth
      :fields="fields"
      :store-prefix="storePrefix"
      :loading="loading"
      :read-only="readOnly"
      :database="database"
      :table="table"
      :view="view"
      @edit-row="openRowEditModal($event)"
      @create-row="openCreateRowModal"
      @row-context="showRowContext($event.event, $event.row)"
      @drop-row="dropRowOnDay($event)"
    ></CalendarMonth>
    <RowEditModal
      ref="rowEditModal"
      enable-navigation
      :database="database"
      :table="table"
      :view="view"
      :all-fields-in-table="fields"
      :primary-is-sortable="false"
      :visible-fields="visibleCardFields"
      :hidden-fields="hiddenFields"
      :rows="allRows"
      :read-only="
        readOnly ||
        (!$hasPermission(
          'database.table.update_row',
          table,
          database.workspace.id
        ) &&
          !$hasPermission(
            'database.table.view.update_row',
            view,
            database.workspace.id
          ))
      "
      :show-hidden-fields="showHiddenFieldsInRowModal"
      @refresh-row="refreshRow"
      @hidden="$emit('selected-row', undefined)"
      @toggle-hidden-fields-visibility="
        showHiddenFieldsInRowModal = !showHiddenFieldsInRowModal
      "
      @update="updateValue"
      @order-fields="orderFields"
      @toggle-field-visibility="toggleFieldVisibility"
      @field-updated="$emit('refresh', $event)"
      @field-deleted="$emit('refresh')"
      @field-created="
        (fieldCreated($event), (showHiddenFieldsInRowModal = true))
      "
      @field-created-callback-done="afterFieldCreatedUpdateFieldOptions"
      @navigate-previous="$emit('navigate-previous', $event)"
      @navigate-next="$emit('navigate-next', $event)"
    ></RowEditModal>
    <Context
      ref="cardContext"
      :overflow-scroll="true"
      :hide-on-scroll="true"
      :hide-on-resize="true"
      :max-height-if-outside-viewport="true"
      class="context__menu-wrapper"
    >
      <ul class="context__menu">
        <TransferRowContextItem
          :database="database"
          :table="table"
          :row="selectedRow"
          :read-only="readOnly"
          @close-context="$refs.cardContext.hide()"
        />
        <li
          v-if="
            !readOnly &&
            ($hasPermission(
              'database.table.delete_row',
              table,
              database.workspace.id
            ) ||
              $hasPermission(
                'database.table.view.delete_row',
                view,
                database.workspace.id
              ))
          "
          class="context__menu-item"
        >
          <a
            class="context__menu-item-link js-ctx-delete-row"
            @click="deleteRow(selectedRow)"
          >
            <i class="context__menu-item-icon iconoir-bin"></i>
            {{ $t('gridView.deleteRow') }}
          </a>
        </li>
      </ul>
    </Context>
  </div>
</template>
<script>
import CalendarMonth from '@baserow_premium/components/views/calendar/CalendarMonth'
import {
  filterHiddenFieldsFunction,
  filterVisibleFieldsFunction,
  sortFieldsByOrderAndIdFunction,
} from '@baserow/modules/database/utils/view'
import { notifyIf } from '@baserow/modules/core/utils/error'
import viewHelpers from '@baserow/modules/database/mixins/viewHelpers'
import TransferRowContextItem from '@baserow/modules/database/components/row/TransferRowContextItem'
import RowEditModal from '@baserow/modules/database/components/row/RowEditModal.vue'
import { populateRow } from '@baserow/modules/database/store/view/grid'
import { clone } from '@baserow/modules/core/utils/object'

export default {
  name: 'CalendarView',
  emits: ['navigate-next', 'navigate-previous', 'refresh', 'selected-row'],
  components: {
    TransferRowContextItem,
    CalendarMonth,
    RowEditModal,
  },
  mixins: [viewHelpers],
  props: {
    database: {
      type: Object,
      required: true,
    },
    table: {
      type: Object,
      required: true,
    },
    view: {
      type: Object,
      required: true,
    },
    fields: {
      type: Array,
      required: true,
    },
    readOnly: {
      type: Boolean,
      required: true,
    },
    loading: {
      type: Boolean,
      required: true,
    },
    storePrefix: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      showHiddenFieldsInRowModal: false,
      selectedRow: null,
      refreshingRow: false,
    }
  },
  computed: {
    row() {
      return this.$store.getters['rowModalNavigation/getRow']
    },
    allRows() {
      return this.$store.getters[`${this.storePrefix}view/calendar/getAllRows`]
    },
    fieldOptions() {
      return this.$store.getters[
        `${this.storePrefix}view/calendar/getAllFieldOptions`
      ]
    },
    getDateField() {
      return this.$store.getters[
        `${this.storePrefix}view/calendar/getDateField`
      ]
    },
    visibleCardFields() {
      return this.fields
        .filter(filterVisibleFieldsFunction(this.fieldOptions))
        .sort(sortFieldsByOrderAndIdFunction(this.fieldOptions))
    },
    hiddenFields() {
      return this.fields
        .filter(filterHiddenFieldsFunction(this.fieldOptions))
        .sort(sortFieldsByOrderAndIdFunction(this.fieldOptions))
    },
  },
  watch: {
    row: {
      deep: true,
      handler(row, oldRow) {
        if (this.$refs.rowEditModal) {
          if (
            (oldRow === null && row !== null) ||
            (oldRow && row && oldRow.id !== row.id)
          ) {
            this.populateAndEditRow(row)
          } else if (oldRow !== null && row === null) {
            // Pass emit=false as argument into the hide function because that will
            // prevent emitting another `hidden` event of the `RowEditModal` which can
            // result in the route changing twice.
            this.$refs.rowEditModal.hide(false)
          }
        }
      },
    },
  },
  mounted() {
    if (this.row !== null) {
      this.populateAndEditRow(this.row)
    }
  },
  methods: {
    async updateValue({ field, row, value, oldValue }) {
      try {
        await this.$store.dispatch(
          this.storePrefix + 'view/calendar/updateRowValue',
          {
            table: this.table,
            view: this.view,
            fields: this.fields,
            row,
            field,
            value,
            oldValue,
          }
        )
      } catch (error) {
        notifyIf(error, 'field')
      }
    },
    /**
     * When the row edit modal is opened we notifiy
     * the Table component that a new row has been selected,
     * such that we can update the path to include the row id.
     */
    openRowEditModal(row) {
      this.$refs.rowEditModal.show(row.id)
      this.$emit('selected-row', row)
    },
    /**
     * Populates a new row and opens the row edit modal
     * to edit the row.
     */
    populateAndEditRow(row) {
      const rowClone = populateRow(clone(row))
      this.$store.dispatch(this.storePrefix + 'view/calendar/selectRow', {
        row: rowClone,
        fields: this.fields,
      })

      this.$refs.rowEditModal.show(row.id, rowClone)
    },
    /**
     * Origem Flow: cartão arrastado e solto em outro dia — muda o valor do
     * campo de data da view para o dia de destino (linha 20 da tabela de
     * ajustes).
     */
    async dropRowOnDay({ day, rowId }) {
      const row = this.allRows.find((r) => r.id === rowId)
      const dateField = this.getDateField(this.fields)
      if (!row || !dateField || day?.date == null) {
        return
      }
      const fieldType = this.$registry.get('field', dateField.type)
      if (!fieldType.canWriteFieldValues(dateField)) {
        return
      }
      const name = `field_${dateField.id}`
      const oldValue = row[name]
      let value
      if (dateField.date_include_time) {
        // Mantém o horário original, mudando apenas o dia.
        const previous = oldValue ? new Date(oldValue) : null
        const target = day.date.toDate ? day.date.toDate() : new Date(day.date)
        if (previous && !isNaN(previous.getTime())) {
          target.setHours(
            previous.getHours(),
            previous.getMinutes(),
            previous.getSeconds(),
            0
          )
        }
        value = target.toISOString()
      } else {
        value = day.date.format ? day.date.format('YYYY-MM-DD') : day.dateString
      }
      if (value === oldValue) {
        return
      }
      await this.updateValue({ field: dateField, row, value, oldValue })
    },
    /**
     * Origem Flow: o "+" do dia cria a tarefa direto (já com a data do
     * dia) e abre o modal padrão da tarefa — o mesmo do clique, com a área
     * de comentários (linha 22 da tabela de ajustes). Antes abria um modal
     * de criação simples, sem comentários.
     */
    async openCreateRowModal(event) {
      const defaults = {}
      const dateField = this.getDateField(this.fields)
      if (!dateField) {
        // Cannot create a row without a proper date field
        return
      }
      const fieldType = this.$registry.get('field', dateField.type)
      if (
        event?.day?.date != null &&
        fieldType.canWriteFieldValues(dateField)
      ) {
        const name = `field_${dateField.id}`
        if (dateField.date_include_time) {
          defaults[name] = event.day.date.toISOString()
        } else {
          defaults[name] = event.day.date.format('YYYY-MM-DD')
        }
      }
      try {
        const row = await this.$store.dispatch(
          this.storePrefix + 'view/calendar/createNewRow',
          {
            view: this.view,
            table: this.table,
            fields: this.fields,
            values: defaults,
          }
        )
        this.populateAndEditRow(row)
      } catch (error) {
        notifyIf(error, 'row')
      }
    },

    showRowContext(event, row) {
      this.selectedRow = row
      this.$refs.cardContext.toggleNextToMouse(event)
    },
    /**
     * Calls action in the store to refresh row directly from the backend - f. ex.
     * when editing row from a different table, when editing is complete, we need
     * to refresh the 'main' row that's 'under' the RowEdit modal.
     */
    refreshRow(row) {
      if (this.refreshingRow) {
        return
      }
      this.refreshingRow = true
      this.$nextTick(async () => {
        try {
          await this.$store.dispatch(
            this.storePrefix + 'view/calendar/refreshRowFromBackend',
            { table: this.table, row }
          )
        } catch (error) {
          notifyIf(error, 'row')
        } finally {
          this.refreshingRow = false
        }
      })
    },
    async deleteRow(row) {
      try {
        this.$refs.cardContext.hide()
        await this.$store.dispatch(
          this.storePrefix + 'view/calendar/deleteRow',
          {
            table: this.table,
            view: this.view,
            fields: this.fields,
            row,
          }
        )
        await this.$store.dispatch('toast/restore', {
          trash_item_type: 'row',
          parent_trash_item_id: this.table.id,
          trash_item_id: row.id,
        })
      } catch (error) {
        notifyIf(error, 'row')
      }
    },
  },
}
</script>
